import { useState, useEffect, useCallback } from 'react';
import { supabase, safeSubscribe } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { calculateFeatureAccess } from '@/utils/user/featureAccess';
import type {
  PrayerCompanionMode,
  PrayerCompanionUsage,
  DailyUsageSummary,
  UsePrayerCompanionUsageReturn,
  PrayerCompanionUsagePayload
} from '@/types/prayerCompanion.types';

/**
 * Hook for tracking Prayer Companion daily usage limits
 */
export function usePrayerCompanionUsage(): UsePrayerCompanionUsageReturn {
  const { user, profile } = useAuth();
  const [dailyUsage, setDailyUsage] = useState<Record<PrayerCompanionMode, DailyUsageSummary>>({
    cards: { mode: 'cards', count: 0, date: getTodayDate(), limit: 0, hasReachedLimit: false, remainingInteractions: 0 },
    chat: { mode: 'chat', count: 0, date: getTodayDate(), limit: 0, hasReachedLimit: false, remainingInteractions: 0 },
    prayer: { mode: 'prayer', count: 0, date: getTodayDate(), limit: 0, hasReachedLimit: false, remainingInteractions: 0 },
    realtime: { mode: 'realtime', count: 0, date: getTodayDate(), limit: 0, hasReachedLimit: false, remainingInteractions: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isIncrementing, setIsIncrementing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Convert Profile to UserProfile format for feature access calculation
  const userProfile = profile ? {
    id: profile.id,
    email: profile.email || '',
    username: profile.username || null,
    role: profile.role_text || 'free',
    name: profile.username || profile.email || '',
    avatarUrl: profile.avatar_url,
    createdAt: profile.created_at || '',
    updatedAt: profile.updated_at,
    isLoggedIn: true,
    isAdmin: profile.role_text === 'staff_admin' || profile.role_text === 'super_admin',
    isNewUser: false,
    displayName: profile.full_name || profile.username || profile.email || ''
  } : null;

  // Get user's feature access to determine limits
  const featureAccess = calculateFeatureAccess(userProfile);
  const dailyLimit = featureAccess.prayerCompanion.dailyInteractionLimit;

  /**
   * Get today's date in YYYY-MM-DD format
   */
  function getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Create daily usage summary for a mode
   */
  const createUsageSummary = useCallback((
    mode: PrayerCompanionMode,
    count: number,
    limit: number
  ): DailyUsageSummary => {
    const hasReachedLimit = limit > 0 && count >= limit;
    const remainingInteractions = limit === -1 ? Infinity : Math.max(0, limit - count);
    
    return {
      mode,
      count,
      date: getTodayDate(),
      limit,
      hasReachedLimit,
      remainingInteractions
    };
  }, []);

  /**
   * Fetch daily usage for current user
   */
  const fetchDailyUsage = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const today = getTodayDate();
      
      // Use direct table query with any type to bypass type checking
      const { data, error: fetchError } = await supabase
        .from('prayer_companion_usage' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('interaction_date', today);
      
      if (fetchError) throw fetchError;
      
      // Create usage summaries for each mode
      const newDailyUsage: Record<PrayerCompanionMode, DailyUsageSummary> = {
        cards: createUsageSummary('cards', 0, dailyLimit),
        chat: createUsageSummary('chat', 0, dailyLimit),
        prayer: createUsageSummary('prayer', 0, dailyLimit),
        realtime: createUsageSummary('realtime', 0, dailyLimit)
      };

      // Update with actual usage data
      if (Array.isArray(data)) {
        data.forEach((usage: any) => {
          if (usage.companion_mode && ['cards', 'chat', 'prayer', 'realtime'].includes(usage.companion_mode)) {
            newDailyUsage[usage.companion_mode as PrayerCompanionMode] = createUsageSummary(
              usage.companion_mode as PrayerCompanionMode,
              usage.interaction_count || 0,
              dailyLimit
            );
          }
        });
      }

      setDailyUsage(newDailyUsage);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching daily usage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, dailyLimit, createUsageSummary]);

  /**
   * Increment usage for a specific mode
   */
  const incrementUsage = useCallback(async (mode: PrayerCompanionMode): Promise<boolean> => {
    if (!user?.id) return false;

    setIsIncrementing(true);
    setError(null);

    try {
      const today = getTodayDate();
      
      // Use direct table upsert with any type to bypass type checking
      const { error: upsertError } = await supabase
        .from('prayer_companion_usage' as any)
        .upsert(
          {
            user_id: user.id,
            companion_mode: mode,
            interaction_date: today,
            interaction_count: dailyUsage[mode].count + 1
          },
          {
            onConflict: 'user_id,companion_mode,interaction_date',
            ignoreDuplicates: false
          }
        );
      
      if (upsertError) throw upsertError;

      // Update local state optimistically
      setDailyUsage(prev => ({
        ...prev,
        [mode]: createUsageSummary(mode, prev[mode].count + 1, dailyLimit)
      }));

      return true;
    } catch (err) {
      setError(err as Error);
      console.error('Error incrementing usage:', err);
      return false;
    } finally {
      setIsIncrementing(false);
    }
  }, [user?.id, dailyUsage, dailyLimit, createUsageSummary]);

  /**
   * Get daily usage for a specific mode
   */
  const getDailyUsage = useCallback((mode: PrayerCompanionMode): DailyUsageSummary => {
    return dailyUsage[mode];
  }, [dailyUsage]);

  /**
   * Check if user can interact with a specific mode
   */
  const canInteract = useCallback((mode: PrayerCompanionMode): boolean => {
    const usage = dailyUsage[mode];
    return !usage.hasReachedLimit;
  }, [dailyUsage]);

  /**
   * Get remaining interactions for a specific mode
   */
  const getRemainingInteractions = useCallback((mode: PrayerCompanionMode): number => {
    return dailyUsage[mode].remainingInteractions;
  }, [dailyUsage]);

  /**
   * Refetch usage data
   */
  const refetchUsage = useCallback(async () => {
    await fetchDailyUsage();
  }, [fetchDailyUsage]);

  // Fetch usage on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      fetchDailyUsage();
    }
  }, [user?.id, fetchDailyUsage]);

  // Set up real-time subscription for usage updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('prayer-companion-usage-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'prayer_companion_usage',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Refetch data when changes occur
          fetchDailyUsage();
        }
      );
      
    safeSubscribe(channel);

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchDailyUsage]);

  return {
    dailyUsage,
    isLoading,
    isIncrementing,
    error,
    incrementUsage,
    getDailyUsage,
    refetchUsage,
    canInteract,
    getRemainingInteractions
  };
}